import { LEGEND_FONT_FAMILY } from "../LegendBox";
import { NOTE_MIN_LINES_PER_SEGMENT } from "../appendNotePages/constants";
import { truncateLabelToFit } from "../truncateLabelToFit";
import { wrapTextToWidth } from "../wrapTextToWidth";
import type { BboxRect } from "components/SmartSurveyor/ReviewPage/types";
import type { CropRegion } from "../computeCropRegion";
import type { LayoutNotePages, NotePageLayout, PlacedNoteCrop } from "./types";

const outlineOnCrop = (
  bbox: BboxRect,
  region: CropRegion,
  crop: PlacedNoteCrop,
): BboxRect => {
  const zoom = crop.side / region.side;
  const clampX = (value: number) =>
    Math.min(Math.max(value, crop.x), crop.x + crop.side);
  const clampY = (value: number) =>
    Math.min(Math.max(value, crop.y), crop.y + crop.side);
  return {
    x1: clampX(crop.x + (bbox.x1 - region.x) * zoom),
    y1: clampY(crop.y + (bbox.y1 - region.y) * zoom),
    x2: clampX(crop.x + (bbox.x2 - region.x) * zoom),
    y2: clampY(crop.y + (bbox.y2 - region.y) * zoom),
  };
};

export const layoutNotePages: LayoutNotePages = ({
  entries,
  regions,
  metrics,
  context,
}) => {
  const {
    contentTop,
    contentBottom,
    entryGap,
    cropX,
    cropSide,
    cardX,
    cardWidth,
    headerHeight,
    headerPadX,
    frameInset,
    bodyPadY,
    lineHeight,
    textWidth,
  } = metrics;
  const bodyFont = `${metrics.bodyFontSize}px ${LEGEND_FONT_FAMILY}`;
  const groupFont = `600 ${metrics.groupFontSize}px ${LEGEND_FONT_FAMILY}`;
  const bylineFont = `${metrics.bylineFontSize}px ${LEGEND_FONT_FAMILY}`;
  const headerWidth = cardWidth - 2 * headerPadX;
  const cardChromeHeight = headerHeight + frameInset + 2 * bodyPadY;

  const pages: NotePageLayout[] = [{ segments: [] }];
  let y = contentTop;
  const startPage = () => {
    pages.push({ segments: [] });
    y = contentTop;
  };
  const linesThatFit = () =>
    Math.max(
      0,
      Math.floor((contentBottom - y - cardChromeHeight) / lineHeight),
    );

  entries.forEach((entry, entryIndex) => {
    const lines = wrapTextToWidth({
      text: entry.content,
      maxWidth: textWidth,
      font: bodyFont,
      context,
    });
    context.font = groupFont;
    const groupLabel = truncateLabelToFit({
      label: entry.groupLabel,
      availableWidth: headerWidth,
      ctx: context,
    });
    context.font = bylineFont;
    const dateSuffix = ` · ${entry.dateLabel}`;
    const author = truncateLabelToFit({
      label: entry.author,
      availableWidth: headerWidth - context.measureText(dateSuffix).width,
      ctx: context,
    });
    const byline = `${author}${dateSuffix}`;

    let lineIndex = 0;
    let isHead = true;
    while (lineIndex < lines.length) {
      const remaining = lines.length - lineIndex;
      const isPageEmpty = y === contentTop;
      const fitsCrop = !isHead || contentBottom - y >= cropSide;
      const capacity = linesThatFit();
      const fitsWhole = fitsCrop && capacity >= remaining;
      // A fresh page takes whatever fits; a partly filled one only a segment worth reading.
      if (
        !fitsWhole &&
        !isPageEmpty &&
        (!fitsCrop || capacity < NOTE_MIN_LINES_PER_SEGMENT)
      ) {
        startPage();
        continue;
      }
      const count = fitsWhole
        ? remaining
        : Math.max(1, Math.min(remaining, capacity));
      const naturalHeight = cardChromeHeight + count * lineHeight;
      const card = {
        x: cardX,
        y,
        width: cardWidth,
        height: isHead ? Math.max(naturalHeight, cropSide) : naturalHeight,
        groupLabel,
        byline,
        lines: lines.slice(lineIndex, lineIndex + count),
      };
      const page = pages[pages.length - 1];
      if (isHead) {
        const crop = { x: cropX, y, side: cropSide };
        page.segments.push({
          kind: "head",
          entryIndex,
          crop,
          outline: outlineOnCrop(entry.bbox, regions[entryIndex], crop),
          card,
        });
      } else {
        page.segments.push({ kind: "continuation", entryIndex, card });
      }

      lineIndex += count;
      isHead = false;
      y += card.height + entryGap;
      if (lineIndex < lines.length) startPage();
    }
  });

  return pages;
};
